import { useState } from 'react';
import { ApiError, api } from '../api/client';
import type { Submission, VerifyRecord } from '../types';

// Shown at the agree-and-submit step. Mirrors the backend's license intent:
// the right to archive + (if later made public) to share.
export const LICENSE_TEXT =
  'I confirm that I own this website or otherwise have the right to archive it, ' +
  'and that I have the right to make the archived copy available to other users ' +
  'of Social Archive if I choose to make it public. I accept responsibility for ' +
  'the legality of capturing and sharing this content.';

function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const p = e.payload as { message?: string; errors?: Record<string, string[]> } | null;
    return (p?.errors && Object.values(p.errors)[0]?.[0]) ?? p?.message ?? `Failed (${e.status}).`;
  }
  return 'Something went wrong.';
}

/**
 * Drives a single draft submission to completion: optional DNS ownership
 * proof → license → submit for review (or discard). Self-contained; the
 * parent owns the draft object and reacts to onChange/onDeleted.
 */
export default function DraftManager({
  draft, onChange, onDeleted,
}: {
  draft: Submission;
  onChange: (s: Submission) => void;
  onDeleted: (id: number) => void;
}) {
  const [busy, setBusy] = useState<null | 'verify' | 'submit' | 'delete'>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [copied, setCopied] = useState(false);

  const rec = draft.verify_record ?? null;
  const verified = draft.domain_verified_at != null;
  const licensed = draft.license_accepted_at != null;

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the record is visible to copy by hand */ }
  }

  async function verify() {
    setBusy('verify');
    setError(null);
    setVerifyMsg(null);
    try {
      const res = await api<{ data: Submission; verified: boolean; verify_record: VerifyRecord }>(
        `/my/submissions/${draft.id}/verify-domain`, { method: 'POST' });
      onChange({ ...draft, ...res.data, verify_record: res.verify_record });
      setVerifyMsg(res.verified
        ? 'Verified — ownership confirmed.'
        : 'TXT record not found yet. DNS can take a few minutes to propagate — try again shortly.');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  }

  async function submit() {
    setBusy('submit');
    setError(null);
    try {
      // Record license acceptance first if not already done, then submit.
      if (!licensed) {
        const lic = await api<{ data: Submission }>(`/my/submissions/${draft.id}/accept-license`, { method: 'POST' });
        onChange({ ...draft, ...lic.data });
      }
      const res = await api<{ data: Submission }>(`/my/submissions/${draft.id}/submit-for-review`, { method: 'POST' });
      onChange({ ...draft, ...res.data });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!window.confirm(`Discard this draft for ${draft.domain}? This can't be undone.`)) return;
    setBusy('delete');
    setError(null);
    try {
      await api(`/my/submissions/${draft.id}`, { method: 'DELETE' });
      onDeleted(draft.id);
    } catch (e) {
      setError(errMsg(e));
      setBusy(null);
    }
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 15 }}>{draft.domain}</strong>
        <span className="status-pill" style={{ background: '#fff7ed', color: '#9a3412' }}>Draft</span>
        {verified && (
          <span className="status-pill" style={{ background: '#dbeafe', color: '#1e40af' }}>✓ owner-verified</span>
        )}
        <span className="muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>{draft.seed_url}</span>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          1. Prove you own the domain{' '}
          <span className="muted" style={{ fontWeight: 400 }}>(optional — speeds up approval)</span>
        </div>
        {rec && (
          <div style={{ marginTop: 6, fontSize: 13 }}>
            <div className="muted">Add this DNS TXT record at your domain, then click Verify:</div>
            <pre style={{ background: 'var(--bg-table-header)', padding: 8, borderRadius: 4, overflowX: 'auto', fontSize: 12, margin: '6px 0' }}>
{`${rec.name}   TXT   "${rec.value}"`}
            </pre>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="secondary" onClick={() => copy(`${rec.name}\tTXT\t${rec.value}`)}>
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button className="secondary" disabled={busy === 'verify' || verified} onClick={verify}>
                {verified ? 'Verified ✓' : busy === 'verify' ? 'Checking…' : 'Verify'}
              </button>
              {verifyMsg && <span className="muted" style={{ fontSize: 12 }}>{verifyMsg}</span>}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>2. Agree &amp; submit for review</div>
        {licensed ? (
          <div className="muted" style={{ fontSize: 12, margin: '6px 0' }}>License accepted ✓</div>
        ) : (
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '8px 0', fontSize: 13, cursor: 'pointer', maxWidth: 560 }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
            <span>{LICENSE_TEXT}</span>
          </label>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={(!licensed && !agreed) || busy === 'submit'} onClick={submit}>
            {busy === 'submit' ? 'Submitting…' : 'Submit for review'}
          </button>
          <button className="secondary" disabled={busy === 'delete'} onClick={remove}>
            {busy === 'delete' ? 'Discarding…' : 'Discard draft'}
          </button>
        </div>
      </div>

      {error && <div className="error" style={{ marginTop: 10 }}>{error}</div>}
    </div>
  );
}
